import { useEffect } from 'react';

import { useOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import type { HyperionFeatureAccess } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { usePropertyDetailsError } from 'src/modules/propertyDetails/hooks/usePropertyDetailsError';
import { Commerce } from 'src/shared/constants/FeatureFlag';
import type {
  EhiFeatureAccess,
  GetEhiOrgSettingsQuery,
} from 'src/shared/graphql/generated';
import {
  EhiFeatureAccessIdentifier,
  FeatureAccessAccessor,
  FeatureAccessIdentifier,
  useGetEhiFeatureAccessesQuery,
  useGetEhiOrgSettingsQuery,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import { useTreatment } from 'src/shared/hooks';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

enum ActiveCommerceProduct {
  ESTIMATION = 'estimation',
  ORDERING_ONLY = 'ordering_only',
  OUT_OF_THE_BOX = 'out_of_the_box_configuration',
}

const getIsEstimationEnabled = (
  ehiFeatureAccesses: Array<EhiFeatureAccess> | undefined,
  isActiveCommerceProductEnabled: boolean,
  activeCommerceProduct?: String,
) => {
  if (
    isActiveCommerceProductEnabled &&
    activeCommerceProduct === ActiveCommerceProduct.ESTIMATION
  ) {
    return true;
  }

  if (!ehiFeatureAccesses) return false;

  let isEstimationEnabled;

  const estimationFeatureAccesses = ehiFeatureAccesses.filter(
    featureAccess =>
      featureAccess.identifier === EhiFeatureAccessIdentifier.Estimation,
  );

  const userFeatureAccess = estimationFeatureAccesses.find(
    featureAccess => featureAccess.accessorType === FeatureAccessAccessor.User,
  );
  const orgFeatureAccess = estimationFeatureAccesses.find(
    featureAccess => featureAccess.accessorType === FeatureAccessAccessor.Org,
  );

  // User feature access takes precedence over org feature access. We need to explicitly
  // check if user feature access is present. If so, use that access level. If not, defer
  // to the org access level.
  if (userFeatureAccess) {
    // if userFeatureAccess exists this trumps all else
    isEstimationEnabled = userFeatureAccess.enabled;
  } else if (orgFeatureAccess) {
    // otherwise determine based on orgFeatureAccess
    isEstimationEnabled = orgFeatureAccess.enabled;
  } else {
    isEstimationEnabled = false;
  }

  return isEstimationEnabled;
};

const getIsMaterialListEnabled = (
  featureAccesses: HyperionFeatureAccess['featureAccesses'] | undefined,
  isActiveCommerceProductEnabled: boolean,
  activeCommerceProduct?: string,
) => {
  if (
    isActiveCommerceProductEnabled &&
    activeCommerceProduct === ActiveCommerceProduct.OUT_OF_THE_BOX
  ) {
    return true;
  }

  if (!featureAccesses) return false;

  return featureAccesses.some(
    featureAccess =>
      featureAccess.identifier ===
      FeatureAccessIdentifier.OutOfTheBoxMaterialLists,
  );
};

const getIsDirectOrderingEnabled = (
  isActiveCommerceProductEnabled: boolean,
  orgSettingsData?: GetEhiOrgSettingsQuery,
  activeCommerceProduct?: string,
) => {
  if (
    isActiveCommerceProductEnabled &&
    activeCommerceProduct === ActiveCommerceProduct.ORDERING_ONLY
  ) {
    return true;
  }

  return orgSettingsData?.ehiOrgSettings?.showOrderingFlow || false;
};

const getIsLeadHomeownerCaptureEnabled = (
  featureAccesses: HyperionFeatureAccess['featureAccesses'] | undefined,
) => {
  const hasLeadHomeownerCapture = featureAccesses?.some(
    featureAccess =>
      featureAccess.identifier === FeatureAccessIdentifier.LeadHomeownerCapture,
  );
  return !!hasLeadHomeownerCapture;
};

const getIsProspectEnabled = (
  featureAccesses: HyperionFeatureAccess['featureAccesses'] | undefined,
) => {
  const hasProspect = featureAccesses?.some(
    featureAccess =>
      featureAccess.identifier === FeatureAccessIdentifier.Prospect,
  );
  return !!hasProspect;
};

const getisBlueprintsEnabled = (
  featureAccesses: HyperionFeatureAccess['featureAccesses'] | undefined,
) => {
  const hasBlueprints = featureAccesses?.some(
    featureAccess =>
      featureAccess.identifier === FeatureAccessIdentifier.Blueprints,
  );
  return !!hasBlueprints;
};

export const useFeatureAccesses = () => {
  const isActiveCommerceProductEnabled = isEnabled(
    useTreatment(Commerce.COMMERCE_ACTIVE_COMMERCE_PRODUCT),
  );
  const {
    data: featureAccessData,
    loading: featureAccessLoading,
    error: featureAccessError,
  } = useGetEhiFeatureAccessesQuery();

  const {
    data: orgSettingsData,
    loading: orgSettingsLoading,
    error: orgSettingsError,
  } = useGetEhiOrgSettingsQuery();
  const { data: profileData, error: profileError } = useProfileQuery();

  const { logError } = usePropertyDetailsError();
  useEffect(() => {
    if (featureAccessError) {
      logError(featureAccessError);
    }
    if (orgSettingsError) {
      logError(orgSettingsError);
    }
    if (profileError) {
      logError(profileError);
    }
  }, [logError, featureAccessError, orgSettingsError, profileError]);

  const { featureAccesses }: HyperionFeatureAccess = useOrg();

  const activeCommerceProduct =
    profileData?.profile?.user.activeCommerceProduct || undefined;

  const isEstimationEnabled = getIsEstimationEnabled(
    featureAccessData?.ehiFeatureAccesses,
    isActiveCommerceProductEnabled,
    activeCommerceProduct,
  );

  const isMaterialListEnabled = getIsMaterialListEnabled(
    featureAccesses,
    isActiveCommerceProductEnabled,
    activeCommerceProduct,
  );

  const loading = featureAccessLoading && orgSettingsLoading;
  const isDirectOrderingEnabled = getIsDirectOrderingEnabled(
    isActiveCommerceProductEnabled,
    orgSettingsData,
    activeCommerceProduct,
  );

  const isLeadHomeownerCaptureEnabled =
    getIsLeadHomeownerCaptureEnabled(featureAccesses);

  const isProspectEnabled = getIsProspectEnabled(featureAccesses);

  const isBlueprintsEnabled = getisBlueprintsEnabled(featureAccesses);

  return {
    isDirectOrderingEnabled,
    isEstimationEnabled,
    isMaterialListEnabled,
    loading,
    isLeadHomeownerCaptureEnabled,
    isProspectEnabled,
    isBlueprintsEnabled,
  };
};
